import { EmeraldSelect } from "@emerald-react/select";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import useSurveyState from "@hooks/useSurveyState";
import { hasAddressLine2AsCustom, hasAddressLine2Field } from "@shared/utils";
import type {
  CustomLegendDisplayFields,
  CustomLegends,
} from "@survey-types/survey.legend.types";
import { updateCurrentSurvey } from "@survey/surveySlice";
import type { KeyboardEvent, MouseEvent } from "react";
import { useEffect } from "react";
import { DetailedSurvey } from "../../types/survey.core.types";

interface LegendDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const LegendDrawer = ({ isOpen, onClose }: LegendDrawerProps) => {
  const { survey, dispatch, updateSurvey } = useSurveyState();
  const isAddressLine2Custom = survey ? hasAddressLine2AsCustom(survey) : false;
  const isAddressLine2Field = hasAddressLine2Field(survey as DetailedSurvey);
  // Options for the dropdown
  const fieldOptions = [
    { label: "Property Name", value: "hasPropertyName" },
    { label: "Address Line 1", value: "hasAddressLine1" },
    ...(isAddressLine2Field && !isAddressLine2Custom
      ? [{ label: "Address Line 2", value: "hasAddressLine2" }]
      : []),
    { label: "City", value: "hasCity" },
    { label: "State", value: "hasState" },
    { label: "Zip Code", value: "hasZipCode" },
  ];

  // Get currently selected values
  const getSelectedValues = () => {
    if (!legendFields) return [];
    return fieldOptions
      .filter(
        (option) =>
          legendFields[option.value as keyof CustomLegendDisplayFields] ===
          true,
      )
      .map((option) => option.value);
  };

  // Get display text for selected values
  const getDisplayText = () => {
    const selectedCount = getSelectedValues().length;
    return `Selected (${selectedCount})`;
  };

  // Handle multi-select dropdown changes
  const handleMultiSelectChange = (
    _event: MouseEvent<Element> | KeyboardEvent<Element>,
    value: string | string[],
  ) => {
    if (!survey) return;

    const selectedValues = Array.isArray(value) ? value : [value];

    // Create updated display fields based on selected values
    const updatedDisplayFields: CustomLegendDisplayFields = {
      hasIcon: legendFields?.hasIcon ?? false,
      hasPropertyName: selectedValues.includes("hasPropertyName"),
      hasAddressLine1: selectedValues.includes("hasAddressLine1"),
      hasAddressLine2: selectedValues.includes("hasAddressLine2"),
      hasCity: selectedValues.includes("hasCity"),
      hasState: selectedValues.includes("hasState"),
      hasZipCode: selectedValues.includes("hasZipCode"),
      hasGroupName: legendFields?.hasGroupName ?? false,
      hasGroupIconOnlyAvailable:
        legendFields?.hasGroupIconOnlyAvailable ?? false,
    };

    const updatedLegends: CustomLegends = {
      ...survey.customLegends,
      displayFields: updatedDisplayFields,
    };

    dispatch(updateCurrentSurvey({ customLegends: updatedLegends }));
    updateSurvey({ id: survey.id, customLegends: updatedLegends }).unwrap();
  };

  // Initialize default legend settings if they don't exist
  useEffect(() => {
    const hasAddressLine2Visible = !(
      survey?.surveyPropertyFieldRule?.propertyFieldRuleList?.find(
        (rule) => rule.fieldKey === "address2",
      )?.isHidden ?? false
    );
    if (survey && !survey.customLegends) {
      const defaultLegends: CustomLegends = {
        hasDisplayLegendOnSeparatePage: false,
        displayDensity: "small",
        displayFields: {
          hasIcon: true,
          hasPropertyName: true,
          hasAddressLine1: true,
          hasAddressLine2: hasAddressLine2Visible,
          hasCity: true,
          hasState: true,
          hasZipCode: true,
          hasGroupName: false,
          hasGroupIconOnlyAvailable: false,
        },
      };
      dispatch(updateCurrentSurvey({ customLegends: defaultLegends }));
      updateSurvey({ id: survey.id, customLegends: defaultLegends }).unwrap();
    }
  }, [survey, dispatch, updateSurvey]);

  if (!survey) {
    return null;
  }

  const legendFields = survey.customLegends?.displayFields;

  return (
    <EmeraldSideSheet
      opened={isOpen}
      title="Legend Display"
      position="right"
      closeOnOutsideClick={true}
      hasCloseButton={true}
      closeOnEscape={true}
      onClose={onClose}
      className="edit-columns"
      hasDivider={true}
    >
      <div className="p-6">
        <div className="space-y-2">
          <label
            htmlFor="display-fields"
            className="block text-sm font-medium text-gray-700"
          >
            Display Fields
          </label>
          <div className="legend-display-override">
            <EmeraldSelect
              title={"Display Fields"}
              editable={false}
              multiple={true}
              options={fieldOptions}
              value={getSelectedValues()}
              onChange={handleMultiSelectChange}
            />
            <div className="custom-display-overlay">
              <span className="text-gray-900">{getDisplayText()}</span>
            </div>
          </div>
        </div>
      </div>
    </EmeraldSideSheet>
  );
};

export default LegendDrawer;
